import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, Calendar, ChevronRight, ChevronDown, Briefcase, GraduationCap, TrendingUp, Clock } from 'lucide-react';
import { experiences, education } from '../data/mockData';
import { useTheme } from '../contexts/ThemeContext';
import { trackButtonClick } from '../utils/trackButtonClick';

/* ── helpers ─────────────────────────────────────────── */

const COMPANY_LOGOS = {
  'OpenText': { src: '/logos/opentext.png', type: 'icon' },
  'Infosys Limited': { src: '/logos/infosys.svg', type: 'wide' },
  'Aricent Technologies (Holdings) Limited (Now Capgemini)': { src: '/logos/aricent.png', type: 'wide' },
  'GlobalLogic India Limited (Now Hitachi Ltd.)': { src: '/logos/globallogic.png', type: 'icon' },
  'EBSAVVY Infotech Private Limited': { src: '/logos/ebsavvy.png', type: 'wide' },
};

const MONTH_MAP = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

const groupByCompany = (exps) => {
  const groups = [];
  const map = {};
  exps.forEach((exp) => {
    if (!map[exp.company]) {
      map[exp.company] = { company: exp.company, roles: [], isCurrent: false };
      groups.push(map[exp.company]);
    }
    map[exp.company].roles.push(exp);
    if (exp.type === 'current') map[exp.company].isCurrent = true;
  });
  return groups;
};

const parseDate = (str) => {
  const m = str.trim().toLowerCase().match(/(\d+)\w*\s+(\w+)\s+(\d{4})/);
  if (!m) return null;
  return new Date(+m[3], MONTH_MAP[m[2]] ?? 0, +m[1]);
};

const calcTenureMonths = (roles) => {
  let min = null;
  let max = null;
  roles.forEach((r) => {
    const [s, e] = r.duration.split(' - ');
    const sd = parseDate(s);
    if (sd && (!min || sd < min)) min = sd;
    if (e.trim().toLowerCase() === 'present') {
      const now = new Date();
      if (!max || now > max) max = now;
    } else {
      const ed = parseDate(e);
      if (ed && (!max || ed > max)) max = ed;
    }
  });
  if (!min || !max) return 0;
  return (max.getFullYear() - min.getFullYear()) * 12 + (max.getMonth() - min.getMonth());
};

const formatTenure = (totalMonths) => {
  if (!totalMonths) return '';
  const y = Math.floor(totalMonths / 12);
  const mo = totalMonths % 12;
  if (y === 0) return `${mo} mo${mo !== 1 ? 's' : ''}`;
  if (mo === 0) return `${y} yr${y !== 1 ? 's' : ''}`;
  return `${y} yr${y !== 1 ? 's' : ''} ${mo} mo`;
};

/* ── component ───────────────────────────────────────── */

const ExperienceSection = () => {
  const cardRefs = useRef([]);
  const { isDark, background, text, accents } = useTheme();
  const [expandedRoles, setExpandedRoles] = useState({});
  const companyGroups = useMemo(() => groupByCompany(experiences), []);
  const tenureMonthsByCompany = useMemo(
    () => companyGroups.map((group) => calcTenureMonths(group.roles)),
    [companyGroups],
  );

  const toggle = (id) =>
    setExpandedRoles((prev) => ({ ...prev, [id]: !prev[id] }));

  /* scroll-reveal */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('exp-visible');
        }),
      { threshold: 0 },
    );
    cardRefs.current.forEach((el) => el && obs.observe(el));
    return () => cardRefs.current.forEach((el) => el && obs.unobserve(el));
  }, []);

  return (
    <section
      id="experience"
      className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}
    >
      {/* subtle bg grid */}
      <div className={`absolute inset-0 ${isDark ? 'opacity-5' : 'opacity-[0.07]'}`}>
        <svg width="100%" height="100%">
          <defs>
            <pattern id="exp-grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#exp-grid)" />
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* ── header ── */}
          <div className="text-center mb-16">
            <div
              className={`inline-block px-4 py-2 ${background.card} rounded-full ${accents.experience} font-mono text-sm mb-6`}
            >
              &gt; career --timeline
            </div>
            <h2
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold ${text.gradient} mb-6 leading-relaxed`}
            >
              Career Highlights
            </h2>
          </div>

          {/* ── company journey cards ── */}
          <div className="space-y-10 mb-20" data-testid="experience-timeline">
            {companyGroups.map((group, gi) => {
              const multi = group.roles.length > 1;
              const logoData = COMPANY_LOGOS[group.company];
              const tenure = formatTenure(tenureMonthsByCompany[gi]);

              return (
                <div
                  key={group.company}
                  ref={(el) => (cardRefs.current[gi] = el)}
                  className="exp-card opacity-0 translate-y-8"
                  style={{ transitionDelay: `${gi * 120}ms` }}
                  data-testid={`company-card-${gi}`}
                >
                  <div
                    className={`relative rounded-2xl overflow-hidden ${background.card} shadow-2xl transition-all duration-500 hover:shadow-blue-500/10 group/card`}
                  >
                    {/* left accent bar */}
                    <div
                      className={`absolute left-0 top-0 bottom-0 w-1 ${
                        group.isCurrent
                          ? 'bg-gradient-to-b from-green-400 via-blue-500 to-purple-500'
                          : isDark
                          ? 'bg-gradient-to-b from-blue-500/70 to-purple-500/50'
                          : 'bg-gradient-to-b from-indigo-400 to-purple-400'
                      }`}
                    />
                    {group.isCurrent && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-green-400 via-blue-500 to-purple-500 blur-sm opacity-60" />
                    )}

                    {/* ── company header ── */}
                    <div
                      className={`px-6 sm:px-8 pt-6 pb-5 ${
                        isDark ? 'border-b border-white/[0.06]' : 'border-b border-indigo-100/60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            {logoData ? (
                              <img
                                src={logoData.src}
                                alt={`${group.company} logo`}
                                className={`max-w-full max-h-full object-contain ${
                                  logoData.type === 'icon' ? 'rounded-lg' : ''
                                } ${logoData.type === 'wide-invert' && isDark ? 'invert brightness-200' : ''}`}
                              />
                            ) : (
                              <span className={`text-2xl font-bold ${accents.experience}`}>
                                {group.company.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <h3 className={`text-xl font-bold ${text.primary}`} data-testid={`company-name-${gi}`}>
                              {group.company}
                            </h3>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-2">
                              {tenure && (
                                <span
                                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-mono font-semibold text-sm border ${
                                    group.isCurrent
                                      ? isDark
                                        ? 'bg-green-500/15 text-green-300 border-green-500/30'
                                        : 'bg-green-50 text-green-700 border-green-200'
                                      : isDark
                                      ? 'bg-amber-500/10 text-amber-300 border-amber-500/25'
                                      : 'bg-amber-50 text-amber-700 border-amber-200'
                                  }`}
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                  {tenure}
                                </span>
                              )}
                              {multi && (
                                <span
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                    isDark
                                      ? 'bg-purple-500/15 text-purple-300'
                                      : 'bg-purple-100 text-purple-700'
                                  }`}
                                >
                                  {group.roles.length} roles
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {group.isCurrent && (
                          <span
                            className={`px-3 py-1.5 text-xs font-bold rounded-full tracking-wide flex-shrink-0 ${
                              isDark
                                ? 'bg-green-500/15 text-green-300 border border-green-500/30 shadow-lg shadow-green-500/10'
                                : 'bg-green-50 text-green-700 border border-green-200 shadow-sm'
                            }`}
                            data-testid={`current-badge-${gi}`}
                          >
                            CURRENT
                          </span>
                        )}
                      </div>
                    </div>

                    {/* ── roles sub-timeline ── */}
                    <div className="px-6 sm:px-8 py-6">
                      {group.roles.map((role, ri) => {
                        const isLast = ri === group.roles.length - 1;
                        const open = !!expandedRoles[role.id];

                        return (
                          <div key={role.id} className="relative" data-testid={`role-item-${role.id}`}>
                            {/* connecting line */}
                            {!isLast && (
                              <div
                                className={`absolute left-[11px] top-6 bottom-0 w-[2px] ${
                                  isDark
                                    ? 'bg-gradient-to-b from-blue-500/40 to-purple-500/20'
                                    : 'bg-gradient-to-b from-indigo-300/50 to-purple-300/30'
                                }`}
                              />
                            )}

                            <div className="flex gap-4">
                              {/* dot */}
                              <div className="relative flex-shrink-0 mt-1.5">
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center ring-4 transition-shadow ${
                                    role.type === 'current'
                                      ? 'bg-green-500 ring-green-500/20 shadow-lg shadow-green-500/30'
                                      : isDark
                                      ? 'bg-blue-500/80 ring-blue-500/10'
                                      : 'bg-indigo-500 ring-indigo-500/10'
                                  }`}
                                >
                                  <div className="w-2 h-2 bg-white rounded-full" />
                                </div>
                              </div>

                              {/* role card */}
                              <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-6'}`}>
                                <div
                                  className={`rounded-xl p-5 cursor-pointer transition-all duration-300 ${
                                    isDark
                                      ? 'bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] hover:border-white/[0.08]'
                                      : 'bg-indigo-50/30 hover:bg-indigo-50/60 border border-indigo-100/40 hover:border-indigo-200/60'
                                  }`}
                                  onClick={() => {
                                    trackButtonClick({
                                      section: 'experience',
                                      label: role.position,
                                      type: 'content_toggle',
                                      detail: role.company
                                    });
                                    toggle(role.id);
                                  }}
                                  data-testid={`role-toggle-${role.id}`}
                                >
                                  {/* role header */}
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                      <h4 className={`text-lg font-bold ${text.primary} leading-snug`}>
                                        {role.position}
                                      </h4>
                                      <div
                                        className={`flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 ${text.muted} text-sm`}
                                      >
                                        <span className="flex items-center font-mono">
                                          <Calendar className="w-3.5 h-3.5 mr-1.5" />
                                          {role.duration}
                                        </span>
                                        <span className="flex items-center">
                                          <MapPin className="w-3.5 h-3.5 mr-1.5" />
                                          {role.location}
                                        </span>
                                      </div>
                                    </div>
                                    <ChevronDown
                                      className={`w-5 h-5 ${text.muted} transition-transform duration-300 flex-shrink-0 mt-1 ${
                                        open ? 'rotate-180' : ''
                                      }`}
                                    />
                                  </div>

                                  {/* expandable achievements */}
                                  <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                      open ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                                    }`}
                                  >
                                    <div
                                      className={`pt-3 border-t ${
                                        isDark ? 'border-white/[0.06]' : 'border-indigo-100/60'
                                      }`}
                                    >
                                      <h5
                                        className={`text-xs font-semibold uppercase tracking-wider ${accents.experience} mb-3 flex items-center`}
                                      >
                                        <Briefcase className="w-3.5 h-3.5 mr-1.5" />
                                        Key Achievements
                                      </h5>
                                      <div className="space-y-2">
                                        {role.responsibilities.map((resp, i) => (
                                          <div
                                            key={i}
                                            className={`flex items-start text-sm ${text.secondary} leading-relaxed`}
                                          >
                                            <ChevronRight
                                              className={`w-3.5 h-3.5 mr-2 mt-0.5 flex-shrink-0 ${accents.experience}`}
                                            />
                                            <span>{resp}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* promotion indicator */}
                                {!isLast && multi && (
                                  <div className="flex items-center gap-2 ml-1 mt-3 mb-1">
                                    <div
                                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                        isDark
                                          ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                          : 'bg-emerald-50 text-emerald-600 border border-emerald-200/60'
                                      }`}
                                    >
                                      <TrendingUp className="w-3 h-3" />
                                      Promoted
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── education ── */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <h3
                className={`text-3xl lg:text-4xl font-bold ${text.gradient} mb-4 flex items-center justify-center`}
              >
                <GraduationCap className="w-8 h-8 text-green-400 mr-3" />
                Education
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {education.map((edu) => (
                <div key={edu.id} className="group relative">
                  <div
                    className={`${background.card} rounded-2xl p-6 shadow-xl hover:shadow-green-500/10 transition-all duration-500 hover:scale-105 text-center`}
                  >
                    <div className="mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl mx-auto flex items-center justify-center shadow-lg shadow-green-500/30">
                        <GraduationCap className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <h4
                      className={`font-bold ${text.primary} mb-2 group-hover:text-green-400 transition-colors`}
                    >
                      {edu.degree}
                    </h4>
                    <p className="text-green-400 font-medium mb-2">{edu.institution}</p>
                    <p className={`${text.secondary} text-sm mb-2`}>{edu.location}</p>
                    <p className={`${text.muted} text-sm font-mono`}>{edu.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* animations */}
      <style>{`
        .exp-card {
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .exp-visible {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </section>
  );
};

export default ExperienceSection;
